import { useState } from 'react'
import { type SWRMutationResponse } from 'swr/mutation'

import { AppError } from '../errors/AppError'

type UseFormProps<T, TResponse, TPath extends string> = {
  initialValues: T
  mutation: SWRMutationResponse<TResponse, Error, TPath, { body: T }>
  onSuccess?: () => void
  /** Optional transform applied to form fields before submit */
  transformBody?: (body: T) => T
}

const useForm = <T extends Record<string, unknown>, TResponse, TPath extends string>({
  initialValues,
  mutation,
  onSuccess,
  transformBody,
}: UseFormProps<T, TResponse, TPath>) => {
  const [fields, setFields] = useState<T>(initialValues)
  const [error, setError] = useState<Error | null>(null)
  const { trigger: submitForm, isMutating: submitting } = mutation

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { type: 'file', name: string, value: File, checked?: boolean } }) => {
    const { name, value, type, checked } = e.target as HTMLInputElement
    setFields(prevFields => {
      if(type === 'file') {
        return { ...prevFields, [name]: value }
      }
      if (type === 'checkbox') {
        const fieldValue = prevFields[name]
        if (Array.isArray(fieldValue)) {
          // Handle checkbox groups
          return {
            ...prevFields,
            [name]: checked
              ? [...fieldValue, value]
              : fieldValue.filter((item: string) => item.toString() !== value.toString())
          }
        } else {
          // Handle single checkbox
          return { ...prevFields, [name]: checked }
        }
      } else if (type === 'radio') {
        return { ...prevFields, [name]: value }
      } else {
        return { ...prevFields, [name]: value }
      }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const body = transformBody ? transformBody(fields) : fields
    try {
      await submitForm({ body }, {
        onSuccess: () => {
          if (onSuccess) {
            onSuccess()
          }
        }
      })
    } catch (error: unknown) {
      if (error instanceof AppError) {
        setError(error)
      } else {
        setError(error as Error)
      }
    }
  }

  return {
    fields,
    error,
    handleChange,
    handleSubmit,
    submitting,
  }
}

export default useForm
